import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';

export const ProductContext = createContext();

const API_BASE = 'https://dummyjson.com';
export const PAGE_SIZE = 12;

const loadStored = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const DEFAULT_FILTERS = {
  category: null,
  search: '',
  sortBy: '',
  order: 'asc',
  minPrice: null,
  maxPrice: null,
};

// dummyjson's /products, /products/search and /products/category/:name endpoints
// all accept sortBy/order/limit/skip, but none of them accept a price range -
// so price filtering has to happen client-side over the full matching set.
const buildUrl = ({ category, search, sortBy, order, limit, skip }) => {
  const base = search
    ? `${API_BASE}/products/search?q=${encodeURIComponent(search)}`
    : category
    ? `${API_BASE}/products/category/${encodeURIComponent(category)}`
    : `${API_BASE}/products`;

  const params = new URLSearchParams();
  if (limit != null) params.set('limit', limit);
  if (skip != null) params.set('skip', skip);
  if (sortBy) {
    params.set('sortBy', sortBy);
    params.set('order', order || 'asc');
  }

  const query = params.toString();
  return query ? `${base}${base.includes('?') ? '&' : '?'}${query}` : base;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const [cartItem, setCartItems] = useState(() => loadStored('store:cart', []));
  const [wishlist, setWishlist] = useState(() => loadStored('store:wishlist', []));

  useEffect(() => {
    window.localStorage.setItem('store:cart', JSON.stringify(cartItem));
  }, [cartItem]);

  useEffect(() => {
    window.localStorage.setItem('store:wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const { category, search, sortBy, order, minPrice, maxPrice } = filters;
    const hasPriceFilter = minPrice != null || maxPrice != null;

    setLoading(true);
    setError(null);

    const url = hasPriceFilter
      ? buildUrl({ category, search, sortBy, order, limit: 0, skip: 0 })
      : buildUrl({ category, search, sortBy, order, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE });

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (hasPriceFilter) {
          const filtered = data.products.filter(
            (p) => p.price >= (minPrice ?? 0) && p.price <= (maxPrice ?? Infinity)
          );
          setTotal(filtered.length);
          setProducts(filtered.slice((page - 1) * PAGE_SIZE, (page - 1) * PAGE_SIZE + PAGE_SIZE));
        } else {
          setTotal(data.total ?? data.products.length);
          setProducts(data.products);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  useEffect(() => {
    fetch(`${API_BASE}/products/category-list`)
      .then((res) => res.json())
      .then((data) => setCategories(data))
      .catch((err) => setError(err));
  }, []);

  const addCartItem = useCallback((item, quantity = 1) => {
    setCartItems((prev) => {
      const existing = prev.find((p) => p.id === item.id);
      if (existing) {
        return prev.map((p) => (p.id === item.id ? { ...p, quantity: p.quantity + quantity } : p));
      }
      return [...prev, { ...item, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    setCartItems((prev) => {
      if (quantity < 1) return prev.filter((p) => p.id !== id);
      return prev.map((p) => (p.id === id ? { ...p, quantity } : p));
    });
  }, []);

  const removeCartItem = useCallback((id) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toggleWishlist = useCallback((item) => {
    setWishlist((prev) =>
      prev.some((p) => p.id === item.id) ? prev.filter((p) => p.id !== item.id) : [...prev, item]
    );
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const value = useMemo(
    () => ({
      products,
      total,
      totalPages,
      page,
      setPage,
      pageSize: PAGE_SIZE,
      categories,
      loading,
      error,
      filters,
      setFilters,
      resetFilters,
      cartItem,
      addCartItem,
      updateQuantity,
      removeCartItem,
      wishlist,
      toggleWishlist,
    }),
    [
      products,
      total,
      totalPages,
      page,
      categories,
      loading,
      error,
      filters,
      setFilters,
      resetFilters,
      cartItem,
      addCartItem,
      updateQuantity,
      removeCartItem,
      wishlist,
      toggleWishlist,
    ]
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};
